// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { DEFAULT_REQUEST_TIMEOUT_MS, requestTimeoutSignal } from "./requestTimeouts";
import { getApiBaseUrl } from "../../authConfig";
import { logAuthFailure } from "../../utils/authDiagnostics";
import { ApiServiceError, type ApiErrorDetails } from "./apiError";
import { ApiResponse } from "./apiResponse";
import { notifySessionExpired } from "../../utils/sessionExpirySignal";
import { stripWireNulls } from "./wireValues";
import { TenantNotResolvedError, isPlaceholderTenantId } from "./tenantGuard";
import {
  buildQueryString,
  readResponseTextOrEmpty,
  trimTrailingSlash,
  type QueryValue,
} from "./apiUtils";

export type ApiMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface ApiRequestOptions<TBody> {
  path: string;
  method?: ApiMethod;
  token?: string;
  headers?: HeadersInit;
  query?: Record<string, QueryValue>;
  body?: TBody;
  signal?: AbortSignal;
}

export class ApiService {
  constructor(private readonly baseUrl: string | (() => string)) {}

  async requestJson<TResponse, TBody = undefined>(
    options: ApiRequestOptions<TBody>,
  ): Promise<ApiResponse<TResponse>> {
    const requestBody = this.serializeBody(options.body);
    const presentedToken = Boolean(options.token);

    const headers = this.buildHeaders(options.headers, options.token, options.body);
    // Never send the placeholder tenant: the API always rejects it (issue 874).
    if (isPlaceholderTenantId(headers.get("X-Tenant-Id"))) {
      console.warn("Request blocked: the workspace tenant has not resolved", {
        path: options.path,
      });
      throw new TenantNotResolvedError(options.path);
    }

    const requestInit: RequestInit = {
      method: options.method ?? "GET",
      headers,
    };

    const signal = options.signal ?? requestTimeoutSignal(DEFAULT_REQUEST_TIMEOUT_MS);
    if (signal !== undefined) {
      requestInit.signal = signal;
    }

    if (requestBody !== undefined) {
      requestInit.body = requestBody;
    }

    const response = await fetch(this.buildUrl(options.path, options.query), {
      ...requestInit,
    });

    if (!response.ok) {
      if (response.status === 401 && presentedToken) {
        logAuthFailure(`api-401 ${options.path}`, { status: response.status });
        notifySessionExpired();
      }
      throw await this.toApiError(response);
    }

    return new ApiResponse<TResponse>(
      response.status,
      response.ok,
      response.headers,
      await this.deserializeJson<TResponse>(response),
    );
  }

  private buildUrl(path: string, query?: Record<string, QueryValue>): string {
    const resolvedBase =
      typeof this.baseUrl === "function" ? this.baseUrl() : this.baseUrl;
    const base = resolvedBase.length > 0 ? trimTrailingSlash(resolvedBase) : "";
    const baseUrl = base.length > 0 ? base : window.location.origin;
    return `${new URL(path, baseUrl).toString()}${buildQueryString(query)}`;
  }

  private buildHeaders(
    headers: HeadersInit | undefined,
    token: string | undefined,
    body: unknown,
  ): Headers {
    const requestHeaders = new Headers(headers);

    if (token) {
      requestHeaders.set("Authorization", `Bearer ${token}`);
    }

    if (body !== undefined && !requestHeaders.has("Content-Type")) {
      requestHeaders.set("Content-Type", "application/json");
    }

    requestHeaders.set("Accept", "application/json");
    return requestHeaders;
  }

  private serializeBody<TBody>(body: TBody | undefined): string | undefined {
    if (body === undefined) {
      return undefined;
    }

    return JSON.stringify(body);
  }

  private async deserializeJson<TResponse>(
    response: Response,
  ): Promise<TResponse> {
    if (response.status === 204) {
      return undefined as TResponse;
    }

    const responseText = await readResponseTextOrEmpty(response);

    if (responseText.length === 0) {
      return undefined as TResponse;
    }

    try {
      return stripWireNulls(JSON.parse(responseText) as TResponse);
    } catch {
      return responseText as TResponse;
    }
  }

  private async toApiError(response: Response): Promise<ApiServiceError> {
    const responseText = await readResponseTextOrEmpty(response);
    const details = this.tryParseErrorDetails(responseText);
    const fallbackMessage = `${response.status} ${response.statusText}`;
    const message =
      details?.detail ??
      details?.title ??
      (responseText.length > 0 ? responseText : fallbackMessage);

    return new ApiServiceError(
      `API request failed (${response.status}): ${message}`,
      response.status,
      response.statusText,
      responseText,
      details,
      response.headers?.get("Retry-After") ?? undefined,
    );
  }

  private tryParseErrorDetails(
    responseText: string,
  ): ApiErrorDetails | undefined {
    if (responseText.length === 0) {
      return undefined;
    }

    try {
      return JSON.parse(responseText) as ApiErrorDetails;
    } catch {
      return undefined;
    }
  }
}

export const apiService = new ApiService(getApiBaseUrl);
